import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import {
  CaseDetail,
  CaseMember,
  CaseSuggestion,
  CaseSummaryRow,
  CaseVoteItem,
  DiscussionMessage,
} from '@/features/caseConnect/types'
import * as Sentry from '@sentry/react'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Error thrown by every Case Connect service call on a non-2xx response.
 * Carries the HTTP `status` so callers can branch on it (e.g. treat a 404 from
 * getCaseByOrder as "no case yet" rather than a hard error) without brittle
 * string matching on the message. Extends Error, so existing callers that only
 * read `.message` keep working unchanged.
 */
export class CaseConnectError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'CaseConnectError'
    this.status = status
  }
}

function withCaller(path: string, caller: string): string {
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}caller=${encodeURIComponent(caller)}`
}

async function okJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    const error = new CaseConnectError(
      response.status,
      `Case Connect request failed: ${response.status} ${response.statusText}`
    )
    Sentry.captureException(error, { tags: { operation: op } })
    throw error
  }
  return (await response.json()) as T
}

/** Fetch a case (summary, anonymized report, vote items) by case id. */
export async function getCase(caseId: string, caller: string): Promise<CaseDetail> {
  return Sentry.startSpan({ op: 'caseConnect.get', name: 'Get Case Detail' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/${caseId}`, caller))
    return okJson<CaseDetail>(response, 'caseConnect.get')
  })
}

/**
 * List the caller's own Clinician Connect cases (the landing view). Backed by
 * GET /case-connect/cases, which returns the UNION of the caller's own case
 * memberships and (for org members) their org's cases — scoping is enforced
 * server-side. Optional `status` filters to OPEN/CLOSED. Returns [] when the
 * caller has no cases.
 */
export async function listMyCases(caller: string, status?: 'OPEN' | 'CLOSED'): Promise<CaseSummaryRow[]> {
  return Sentry.startSpan({ op: 'caseConnect.listMine', name: 'List My Cases' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const path = status ? `/case-connect/cases?status=${encodeURIComponent(status)}` : '/case-connect/cases'
    const response = await client.get(withCaller(path, caller))
    const data = await okJson<{ cases: CaseSummaryRow[] }>(response, 'caseConnect.listMine')
    return data.cases ?? []
  })
}

/** Resolve the case for an order (the page is keyed by orderId). */
export async function getCaseByOrder(orderId: string, caller: string): Promise<CaseDetail> {
  return Sentry.startSpan({ op: 'caseConnect.getByOrder', name: 'Get Case By Order' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/orders/${orderId}/case`, caller))
    return okJson<CaseDetail>(response, 'caseConnect.getByOrder')
  })
}

/** Create a case for an order (idempotent per order). Server derives the order from the path. */
export async function createCaseForOrder(orderId: string, caller: string): Promise<{ caseId: string }> {
  return Sentry.startSpan({ op: 'caseConnect.create', name: 'Create Case For Order' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(withCaller(`/case-connect/orders/${orderId}/case`, caller), {}, JSON_HEADERS)
    return okJson<{ caseId: string }>(response, 'caseConnect.create')
  })
}

/** Close a case (case manager only). */
export async function closeCase(caseId: string, caller: string): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.close', name: 'Close Case' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(withCaller(`/case-connect/${caseId}/close`, caller), {}, JSON_HEADERS)
    await okJson(response, 'caseConnect.close')
  })
}

/** List current case participants. */
export async function listMembers(caseId: string, caller: string): Promise<CaseMember[]> {
  return Sentry.startSpan({ op: 'caseConnect.members.list', name: 'List Case Members' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/${caseId}/members`, caller))
    const data = await okJson<{ members: CaseMember[] }>(response, 'caseConnect.members.list')
    return data.members ?? []
  })
}

/** Invite clinicians by email with a case role. */
export async function inviteMembers(
  caseId: string,
  emails: string[],
  roleKey: string,
  caller: string
): Promise<{ invited: string[]; count: number }> {
  return Sentry.startSpan({ op: 'caseConnect.members.invite', name: 'Invite Case Members' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(
      withCaller(`/case-connect/${caseId}/members`, caller),
      { emails, roleKey },
      JSON_HEADERS
    )
    return okJson<{ invited: string[]; count: number }>(response, 'caseConnect.members.invite')
  })
}

/** Remove a participant from the case. */
export async function removeMember(caseId: string, userId: string, caller: string): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.members.remove', name: 'Remove Case Member' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.delete(withCaller(`/case-connect/${caseId}/members/${userId}`, caller))
    await okJson(response, 'caseConnect.members.remove')
  })
}

/** List discussion messages. */
export async function listDiscussion(caseId: string, caller: string): Promise<DiscussionMessage[]> {
  return Sentry.startSpan({ op: 'caseConnect.discussion.list', name: 'List Discussion' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/${caseId}/discussion`, caller))
    const data = await okJson<{ messages: DiscussionMessage[] }>(response, 'caseConnect.discussion.list')
    return data.messages ?? []
  })
}

/** Post a discussion message. */
export async function postDiscussion(caseId: string, body: string, caller: string): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.discussion.post', name: 'Post Discussion' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(withCaller(`/case-connect/${caseId}/discussion`, caller), { body }, JSON_HEADERS)
    await okJson(response, 'caseConnect.discussion.post')
  })
}

/** Edit own discussion message. */
export async function editDiscussion(
  caseId: string,
  discussionId: string,
  body: string,
  caller: string
): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.discussion.edit', name: 'Edit Discussion' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.patch(
      withCaller(`/case-connect/${caseId}/discussion/${discussionId}`, caller),
      { body },
      JSON_HEADERS
    )
    await okJson(response, 'caseConnect.discussion.edit')
  })
}

/** List votable care-plan items with tallies. */
export async function listVoteItems(caseId: string, caller: string): Promise<CaseVoteItem[]> {
  return Sentry.startSpan({ op: 'caseConnect.voteItems.list', name: 'List Vote Items' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/${caseId}/vote-items`, caller))
    const data = await okJson<{ voteItems: CaseVoteItem[] }>(response, 'caseConnect.voteItems.list')
    return data.voteItems ?? []
  })
}

/** Cast (or change) an UP/DOWN vote on an item. */
export async function castVote(
  caseId: string,
  voteItemId: string,
  value: 'UP' | 'DOWN',
  caller: string
): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.vote', name: 'Cast Vote' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(
      withCaller(`/case-connect/${caseId}/vote-items/${voteItemId}/vote`, caller),
      { value },
      JSON_HEADERS
    )
    await okJson(response, 'caseConnect.vote')
  })
}

/** List suggestions. */
export async function listSuggestions(caseId: string, caller: string): Promise<CaseSuggestion[]> {
  return Sentry.startSpan({ op: 'caseConnect.suggestions.list', name: 'List Suggestions' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(withCaller(`/case-connect/${caseId}/suggestions`, caller))
    const data = await okJson<{ suggestions: CaseSuggestion[] }>(response, 'caseConnect.suggestions.list')
    return data.suggestions ?? []
  })
}

/** Add a free-text suggestion (spawns a votable item). */
export async function addSuggestion(
  caseId: string,
  body: string,
  rationale: string | null,
  caller: string
): Promise<void> {
  return Sentry.startSpan({ op: 'caseConnect.suggestions.add', name: 'Add Suggestion' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(
      withCaller(`/case-connect/${caseId}/suggestions`, caller),
      { body, rationale },
      JSON_HEADERS
    )
    await okJson(response, 'caseConnect.suggestions.add')
  })
}
